import katex from "katex"
import { EXAMPLES } from "./utils.js"
import CropView from "./CropView.jsx"
import IconScreenshot from "../images/icon-screenshot.svg?react"
import IconPick from "../images/icon-pick.svg?react"
import IconAttach from "../images/icon-attach.svg?react"
import IconArrowRight from "../images/icon-arrow-right.svg?react"
import IconInequality from "../images/icon-inequality.svg?react"
import IconGeometry from "../images/icon-geometry.svg?react"
import IconCalculus from "../images/icon-calculus.svg?react"
import IconChevronRight from "../images/icon-chevron-right.svg?react"

const SAMPLE_ICONS = {
  inequality: IconInequality,
  geometry: IconGeometry,
  calculus: IconCalculus,
}

const katexHtmlCache = new Map()
function renderKatex(answer) {
  const cached = katexHtmlCache.get(answer)
  if (cached !== undefined) return cached
  const html = katex.renderToString(answer, { throwOnError: false })
  katexHtmlCache.set(answer, html)
  return html
}

function SampleCard({ accent, iconKey, title, answer, tags, onClick }) {
  const Icon = SAMPLE_ICONS[iconKey] || IconInequality
  return (
    <button type="button" className={`ds-list-item item-${accent}`} onClick={onClick} title={title}>
      <div className={`ds-card-glow glow-${accent}`} />
      <div className="ds-li-inner">
        <div className={`ds-icon-box glow-${accent}`}>
          <div className="ds-icon-box-inner">
            <Icon width={20} height={20} />
          </div>
        </div>
        <div className="ds-li-content">
          <div className="ds-li-title">{title}</div>
          {answer && <div className="ds-li-answer" dangerouslySetInnerHTML={{ __html: renderKatex(answer) }} />}
          {tags && tags.length > 0 && (
            <div className="ds-li-tags">
              {tags.map((tag) => (
                <span key={tag} className="tag neutral">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="ds-li-arrow">
          <IconChevronRight width={18} height={18} />
        </div>
      </div>
    </button>
  )
}

export default function MainPage({
  config,
  history,
  problem,
  setProblem,
  onSubmit,
  onHistoryClick,
  onExampleClick,
  onClearHistory,
  solverStatus,
  onScreenshot,
  onAttach,
  onPick,
  screenshot,
  onCropDone,
  onCropCancel,
  recognizing,
  attachError,
  onClearAttachError,
}) {
  if (!config) return null
  const isPending = solverStatus === "PENDING"
  const isCropping = !!screenshot
  const canSubmit = problem.trim().length > 0 && !isPending && !isCropping && !recognizing

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      if (canSubmit) onSubmit()
    }
  }

  const showExamples = history.length < 3

  const renderCards = (entries, onCardClick) =>
    entries.map((entry, index) => (
      <SampleCard
        key={`${entry.equation}|${entry.region ?? ""}|${entry.level ?? ""}|${index}`}
        accent={entry.accent || "purple"}
        iconKey={entry.iconKey || "inequality"}
        title={entry.label || entry.equation}
        answer={entry.answer}
        tags={[entry.levelLabel || entry.level, entry.region].filter(Boolean)}
        onClick={() => (onCardClick ? onCardClick(entry) : setProblem(entry.equation))}
      />
    ))

  return (
    <div className="main-page">
      <div className={isCropping ? "home-top-crop" : "home-top-fixed"}>
        {isCropping ? (
          <CropView screenshot={screenshot} onCrop={onCropDone} onCancel={onCropCancel} />
        ) : (
          <>
            <div className="input-wrapper">
              <div className="textarea-wrap">
                <textarea
                  className="equation-textarea"
                  value={problem}
                  onChange={(e) => setProblem(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter a math problem or paste an equation…"
                  rows={3}
                  disabled={recognizing}
                />
                {recognizing && (
                  <div className="recognizing-overlay">
                    <div className="dots">
                      <span />
                      <span />
                      <span />
                    </div>
                    <span className="recognizing-label">Recognizing equation…</span>
                  </div>
                )}
              </div>
              <div className="input-toolbar">
                <button className="tool-btn" title="screenshot" onClick={onScreenshot}>
                  <IconScreenshot width={15} height={15} />
                  Screenshot
                </button>
                <button className="tool-btn" title="pick from page" onClick={onPick}>
                  <IconPick width={15} height={15} />
                  Pick
                </button>
                <label className="tool-btn" title="attach">
                  <IconAttach width={15} height={15} />
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0]
                      if (file) onAttach(file)
                      event.target.value = ""
                    }}
                  />
                </label>
              </div>
            </div>
            {attachError && (
              <div className="attach-error" role="alert">
                <span>{attachError}</span>
                <button type="button" onClick={onClearAttachError} aria-label="Dismiss">
                  ×
                </button>
              </div>
            )}
            <button className="solve-btn-main" onClick={onSubmit} disabled={!canSubmit}>
              <span>{isPending ? "Solving…" : "Solve"}</span>
              <IconArrowRight width={20} height={20} />
            </button>
          </>
        )}
      </div>

      {!isCropping && (
        <div className="home-scroll-wrapper">
          <div className="home-scroll-content">
            {history.length > 0 && (
              <div className="section-container">
                <div className="picker-heading picker-heading--row">
                  <span>Recent queries</span>
                  {__DEBUG__ && onClearHistory && (
                    <button
                      type="button"
                      className="picker-heading-clear"
                      onClick={onClearHistory}
                      title="Clear history (dev only)"
                      aria-label="Clear history"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="samples-list">{renderCards(history, onHistoryClick)}</div>
              </div>
            )}
            {showExamples && (
              <div className="section-container">
                <div className="picker-heading">Try examples</div>
                <div className="samples-list">{renderCards(EXAMPLES, onExampleClick)}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
